import {
  action,
  computed,
  makeObservable,
  observable,
  reaction,
  runInAction,
} from 'mobx';
import { LayerGroupNode } from './LayerGroupNode';
import { RootNode } from './RootNode';
import {
  APITableLayer,
  Node,
  NodeStatus,
  TreeContext,
  TreeServices,
} from './types';
import { Table as APITable } from '@datum-group/isogd-openapi/lib/';
import { RecordNode } from './RecordNode';
import { Filter } from './Filter';

const DEFAULT_QUERY = {
  _limit: 20,
  _offset: 0,
};

export class TableLayerNode implements Node {
  readonly parent: LayerGroupNode | RootNode;
  readonly filter: Filter;
  @observable private _table?: APITable = undefined;
  @observable private _children: RecordNode[] = [];
  @observable private _childrenStatus = NodeStatus.notLoaded;
  @observable private _error: string | undefined = undefined;
  @observable private _totalRecords = 0;
  @observable private _totalQueryRecords = 0;
  private _layer: APITableLayer;
  private _context: TreeContext;
  private _services: TreeServices;

  constructor(
    layer: APITableLayer,
    parent: LayerGroupNode | RootNode,
    context: TreeContext,
    services: TreeServices,
  ) {
    this._services = services;
    this._context = context;
    this.filter = new Filter({
      ...DEFAULT_QUERY,
      ...this._context.tableDefaultFilter,
    });
    this._layer = layer;
    this.parent = parent;
    makeObservable(this);
    reaction(
      () => this.filter.query,
      () => this._loadRecords(),
    );
  }

  @computed get layer(): APITableLayer {
    return JSON.parse(JSON.stringify(this._layer));
  }

  @computed get name() {
    return this._layer.layer_name;
  }

  @computed get description() {
    return this._layer.description;
  }

  @computed get table(): APITable | undefined {
    return this._table ? JSON.parse(JSON.stringify(this._table)) : undefined;
  }

  @computed get tableName() {
    return this._table?.table_name;
  }

  @computed get tableDescription() {
    return this._table?.description;
  }

  @computed get tableDisplay() {
    return this._table?.display;
  }

  @computed get children(): RecordNode[] {
    return [...this._children];
  }

  @computed get childrenStatus() {
    return this._childrenStatus;
  }

  @computed get error() {
    return this._error;
  }

  @computed get totalRecords() {
    return this._totalRecords;
  }

  @computed get totalQueryRecords() {
    return this._totalQueryRecords;
  }

  @computed get imageSrc(): string | undefined {
    return this._layer?.icon;
  }

  @action async load() {
    this._childrenStatus = NodeStatus.loading;
    this._error = undefined;

    try {
      const response = await this._services.table.apiTablesRead(
        this._layer.data_source,
      );

      runInAction(() => (this._table = response.data));
    } catch (e) {
      runInAction(() => {
        this._error = 'Не удалось загрузить информацию о таблице';
      });
    } finally {
      runInAction(() => (this._childrenStatus = NodeStatus.notLoaded));
    }

    await this._loadRecords();
  }

  @action private async _loadRecords() {
    if (!this._table) return;

    this._childrenStatus = NodeStatus.loading;
    this._error = undefined;

    try {
      const { _page, _page_size } = this.filter.pagination;
      const query = this.filter.param;

      const response = await this._services.records.apiGeojsonList(
        this._layer.data_source,
        _page,
        _page_size,
        {
          query,
        },
      );

      const records = response.data;
      // @ts-ignore
      const total = records._from_count || 0;
      // @ts-ignore
      const totalQuery = records._count || 0;
      const children = records.features.map(
        (record) => new RecordNode(record, this),
      );

      runInAction(() => {
        this._totalRecords = total;
        this._totalQueryRecords = totalQuery;
        this._children = children;
        this._childrenStatus = NodeStatus.loaded;
      });
    } catch (e) {
      runInAction(() => {
        this._childrenStatus = NodeStatus.notLoaded;
        this._error = 'Не удалось загрузить объекты';
      });
    }
  }
}
